import { ArrowUturnLeftIcon } from "@heroicons/react/20/solid";
import {
    ChevronRightIcon,
    CursorArrowRaysIcon,
    EnvelopeIcon,
    EnvelopeOpenIcon,
    PaperClipIcon,
} from "@heroicons/react/24/outline";
import {
    EmailEntityId,
    relativeTimeAgo,
    ReplyAction,
    SentMessageId,
    SentMessageType,
    UserId,
    UserMailboxMessageId,
} from "@sp-crm/core";
import { Button } from "components/layout/button";
import { ClientLink, CommunityLink } from "components/ui/entity-link";
import { defaultLinkStyle } from "components/ui/link";
import {
    AdvancedSearchEntityType,
    ConversationFeedBasicsFragment,
    SentMessageFragmentFragment,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useCurrentUserId, useFeature, useUsers } from "store/selectors/hooks";
import { getSanitizedMarkUpFromEmailBody } from "../../util/email";
import { handleEvent } from "../../util/user-events";
import { useUserMailboxes } from "./use-user-mailboxes";
import {
    buildReplyFromReceived,
    buildReplyFromSent,
    isCurrentUserEmail,
    ReplyState,
} from "./email-reply-utils";
import { MessageCompose } from "./message-compose";
import { ReceivedMessage } from "./received-message-detail";
import { ReceivedMessageInlineDetailLegacy } from "./received-message-inline-detail-legacy";
import { SentMessageInlineDetailLegacy } from "./sent-message-inline-detail-legacy";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";
import {
    MessageBodyData,
    useReceivedMessageBody,
    useSentMessageBody,
} from "./use-message-body";

type EmailThreadType = NonNullable<
    ConversationFeedBasicsFragment["threads"][number]["emailThread"]
>;
type MailboxMessageType = EmailThreadType["mailboxMessages"][number];

interface ThreadMessage {
    id: string;
    type: "sent" | "received";
    timestamp: Date;
    senderName: string;
    bodyPreview: string;
    attachmentCount: number;
    openedCount: number;
    clickCount: number;
    originalSent?: SentMessageFragmentFragment;
    originalReceived?: MailboxMessageType;
}

interface UserLookup {
    [userId: string]: { name: string } | undefined;
}

function getSenderName(
    sent: SentMessageFragmentFragment,
    currentUserId: UserId | null,
    users: UserLookup,
): string {
    if (sent.userId === currentUserId) {
        return "You";
    }
    if (sent.userId && users[sent.userId]) {
        return users[sent.userId]?.name ?? "Unknown";
    }
    return "Unknown";
}

function getOpenedCount(sent: SentMessageFragmentFragment): number {
    return sent.events ? sent.events.filter(e => e.eventType === "opened").length : 0;
}

function getClickCount(sent: SentMessageFragmentFragment): number {
    return (
        sent.clickedLinkEvents?.reduce((acc, e) => acc + (e.eventsV2?.length || 0), 0) ||
        0
    );
}

function getReceivedSenderName(
    received: MailboxMessageType,
    currentUserEmails: string[],
): string {
    const fromEmail = received.from?.email;
    if (fromEmail && isCurrentUserEmail(fromEmail, currentUserEmails)) {
        return "You";
    }
    return (
        received.sender?.resolvedName ||
        received.sender?.name ||
        received.from?.resolvedName ||
        received.from?.name ||
        "Unknown"
    );
}

function buildThreadMessages(
    emailThread: EmailThreadType,
    currentUserId: UserId | null,
    users: UserLookup,
    currentUserEmails: string[],
): ThreadMessage[] {
    const messages: ThreadMessage[] = [];

    for (const sent of emailThread.sentMessages) {
        messages.push({
            id: sent.id,
            type: "sent",
            timestamp: new Date(sent.createdAt),
            senderName: getSenderName(sent, currentUserId, users),
            bodyPreview: sent.bodyPreview ?? sent.subject ?? "",
            attachmentCount: sent.attachments?.length ?? 0,
            openedCount: getOpenedCount(sent),
            clickCount: getClickCount(sent),
            originalSent: sent,
        });
    }

    for (const received of emailThread.mailboxMessages) {
        messages.push({
            id: received.id,
            type: "received",
            timestamp: new Date(received.receivedTimestamp),
            senderName: getReceivedSenderName(received, currentUserEmails),
            bodyPreview: received.bodyPreview,
            attachmentCount: received.attachmentCount,
            openedCount: 0,
            clickCount: 0,
            originalReceived: received,
        });
    }

    messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return messages;
}

function getThreadSubject(allMessages: ThreadMessage[]): string {
    if (allMessages.length === 0) {
        return "(no subject)";
    }
    const oldest = allMessages[allMessages.length - 1];
    if (oldest.type === "sent" && oldest.originalSent) {
        return oldest.originalSent.subject || "(no subject)";
    }
    if (oldest.type === "received" && oldest.originalReceived) {
        return oldest.originalReceived.subject || "(no subject)";
    }
    return "(no subject)";
}

function formatQuotedDate(date: Date): string {
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function escapeHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildQuotedBody(senderName: string, timestamp: Date, bodyHtml: string): string {
    const dateStr = formatQuotedDate(timestamp);
    const safeName = escapeHtml(senderName);
    return [
        "<br/>",
        `<div>On ${dateStr}, ${safeName} wrote:</div>`,
        '<blockquote style="margin:0 0 0 .8ex;border-left:1px #ccc solid;padding-left:1ex">',
        bodyHtml,
        "</blockquote>",
    ].join("");
}

interface SentMessageStatusProps {
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
}

const SentMessageStatusIndicator: React.FC<SentMessageStatusProps> = props => {
    const { status, latestEventStatus, messageType } = props;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

function toReceivedMessage(mailboxMessage: MailboxMessageType): ReceivedMessage {
    return {
        id: mailboxMessage.id,
        receivedTimestamp: mailboxMessage.receivedTimestamp,
        subject: mailboxMessage.subject,
        bodyPreview: mailboxMessage.bodyPreview,
        attachmentCount: mailboxMessage.attachmentCount,
        from: mailboxMessage.from,
        to: mailboxMessage.to,
        cc: mailboxMessage.cc,
        bcc: mailboxMessage.bcc,
    };
}

const ReplyAllIcon: React.FC<{ className?: string }> = ({ className }) => (
    <span
        data-slot="icon"
        className={`inline-flex items-center -space-x-1 !w-auto ${className ?? ""}`}>
        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
    </span>
);

interface InlineReplyButtonsProps {
    message: ThreadMessage;
    onReply: (message: ThreadMessage, body: MessageBodyData) => void;
    onReplyAll: (message: ThreadMessage, body: MessageBodyData) => void;
    body: MessageBodyData;
}

const InlineReplyButtons: React.FC<InlineReplyButtonsProps> = props => {
    const { message, onReply, onReplyAll, body } = props;
    return (
        <div className="flex items-center gap-0 flex-shrink-0">
            <Button
                type="button"
                plain
                title="Reply"
                className="inline-flex items-center text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onReply(message, body);
                }}>
                <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
                type="button"
                plain
                title="Reply All"
                className="inline-flex items-center text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onReplyAll(message, body);
                }}>
                <ReplyAllIcon />
            </Button>
        </div>
    );
};

interface ReceivedMessageReplyButtonsProps {
    message: ThreadMessage;
    entityId: EmailEntityId | undefined;
    onReply: (message: ThreadMessage, body: MessageBodyData) => void;
    onReplyAll: (message: ThreadMessage, body: MessageBodyData) => void;
}

const ReceivedMessageReplyButtons: React.FC<ReceivedMessageReplyButtonsProps> = props => {
    const { message, entityId, onReply, onReplyAll } = props;
    const body = useReceivedMessageBody(entityId, message.id as UserMailboxMessageId);
    return (
        <InlineReplyButtons
            message={message}
            onReply={onReply}
            onReplyAll={onReplyAll}
            body={body}
        />
    );
};

interface SentMessageReplyButtonsProps {
    message: ThreadMessage;
    messageId: SentMessageId;
    onReply: (message: ThreadMessage, body: MessageBodyData) => void;
    onReplyAll: (message: ThreadMessage, body: MessageBodyData) => void;
}

const SentMessageReplyButtons: React.FC<SentMessageReplyButtonsProps> = props => {
    const { message, messageId, onReply, onReplyAll } = props;
    const body = useSentMessageBody(messageId);
    return (
        <InlineReplyButtons
            message={message}
            onReply={onReply}
            onReplyAll={onReplyAll}
            body={body}
        />
    );
};

interface EmailThreadComponentLegacyProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    entityId?: EmailEntityId;
    entityType?: AdvancedSearchEntityType;
}

const MAX_VISIBLE_MESSAGES = 3;

export const EmailThreadComponentLegacy: React.FC<
    EmailThreadComponentLegacyProps
> = props => {
    const { thread, entityId, entityType } = props;
    const emailThread = thread.emailThread;
    const [expanded, setExpanded] = useState(false);
    const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set());
    const [replyState, setReplyState] = useState<ReplyState | null>(null);

    const currentUserId = useCurrentUserId();
    const usersState = useUsers();
    const enhancedMode = useFeature("enhancedSentMessageView");
    const { activeMailbox } = useUserMailboxes();

    const canReply = !!activeMailbox && !!entityId && !!entityType;
    const currentUserEmail = activeMailbox?.email;
    const currentUserAccountEmail = currentUserId
        ? usersState.users[currentUserId]?.email
        : undefined;

    const currentUserEmails = useMemo(() => {
        const emails: string[] = [];
        if (currentUserEmail) emails.push(currentUserEmail);
        if (
            currentUserAccountEmail &&
            (!currentUserEmail ||
                currentUserAccountEmail.toLowerCase() !== currentUserEmail.toLowerCase())
        ) {
            emails.push(currentUserAccountEmail);
        }
        return emails;
    }, [currentUserEmail, currentUserAccountEmail]);

    const allMessages = useMemo(() => {
        if (!emailThread) return [];
        return buildThreadMessages(
            emailThread,
            currentUserId,
            usersState.users,
            currentUserEmails,
        );
    }, [emailThread, currentUserId, usersState.users, currentUserEmails]);

    const handleToggleExpand = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setExpanded(prev => !prev);
    }, []);

    const toggleMessageExpanded = useCallback((messageId: string) => {
        setExpandedMessageIds(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    }, []);

    const intakeContext = useMemo(() => {
        const sentMessages = emailThread?.sentMessages ?? [];
        for (const sent of sentMessages) {
            if (sent.intakeClient || sent.intakeCommunity) {
                return {
                    client: sent.intakeClient,
                    community: sent.intakeCommunity,
                };
            }
        }
        return null;
    }, [emailThread?.sentMessages]);

    const stopPropagation = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    if (!emailThread) {
        return null;
    }

    const totalCount = allMessages.length;
    const hiddenCount = totalCount - MAX_VISIBLE_MESSAGES;
    const visibleMessages = expanded
        ? allMessages
        : allMessages.slice(0, MAX_VISIBLE_MESSAGES);

    const subject = getThreadSubject(allMessages);

    const client =
        entityType === AdvancedSearchEntityType.Community ? intakeContext?.client : null;
    const community =
        entityType === AdvancedSearchEntityType.Client ? intakeContext?.community : null;

    const buildReplyForMessage = (
        message: ThreadMessage,
        replyType: ReplyAction,
        body: MessageBodyData,
    ): ReplyState | null => {
        const quotedSenderName =
            message.senderName === "You"
                ? (usersState.users[currentUserId!]?.name ?? "You")
                : message.senderName;

        let contentHtml: string;
        if (body?.html) {
            contentHtml = getSanitizedMarkUpFromEmailBody(body.html).__html;
        } else if (body?.plain) {
            contentHtml = escapeHtml(body.plain).replace(/\n/g, "<br/>");
        } else {
            contentHtml = escapeHtml(message.bodyPreview).replace(/\n/g, "<br/>");
        }

        const quotedBody = buildQuotedBody(
            quotedSenderName,
            message.timestamp,
            contentHtml,
        );

        if (message.type === "received" && message.originalReceived) {
            const base = buildReplyFromReceived(
                message.originalReceived,
                subject,
                replyType,
                currentUserEmails,
            );
            return { ...base, initialBody: quotedBody };
        }
        if (message.type === "sent" && message.originalSent) {
            const base = buildReplyFromSent(
                message.originalSent,
                subject,
                replyType,
                currentUserEmails,
            );
            return { ...base, initialBody: quotedBody };
        }
        return null;
    };

    const handleReply = (message: ThreadMessage, body: MessageBodyData) => {
        const state = buildReplyForMessage(message, ReplyAction.Reply, body);
        if (state) {
            handleEvent("message-reply");
            setReplyState(state);
        }
    };

    const handleReplyAll = (message: ThreadMessage, body: MessageBodyData) => {
        const state = buildReplyForMessage(message, ReplyAction.ReplyAll, body);
        if (state) {
            handleEvent("message-reply-all");
            setReplyState(state);
        }
    };

    return (
        <li key={thread.id}>
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                <div className="flex items-center gap-3 px-3 py-2">
                    <EnvelopeIcon
                        className="h-4 w-4 text-gray-400 flex-shrink-0"
                        aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 truncate">
                            {subject}
                        </span>
                        {community && (
                            <CommunityLink
                                communityId={community.id}
                                name={community.name ?? "(no name)"}
                                className="flex-shrink-0"
                                onClick={stopPropagation}
                            />
                        )}
                        {client && (
                            <ClientLink
                                clientId={client.id}
                                name={
                                    client.name ?? client.bestContactName ?? "(no name)"
                                }
                                className="flex-shrink-0"
                                onClick={stopPropagation}
                            />
                        )}
                    </div>
                </div>
                <div>
                    {visibleMessages.map(message => {
                        const isExpanded = expandedMessageIds.has(message.id);

                        if (isExpanded) {
                            if (message.type === "sent" && message.originalSent) {
                                return (
                                    <SentMessageInlineDetailLegacy
                                        key={message.id}
                                        messageId={
                                            message.originalSent.id as SentMessageId
                                        }
                                        attachmentCount={message.attachmentCount}
                                        status={message.originalSent.status}
                                        latestEventStatus={
                                            message.originalSent.latestEventStatus
                                        }
                                        messageType={
                                            message.originalSent
                                                .messageType as SentMessageType
                                        }
                                        onCollapse={() =>
                                            toggleMessageExpanded(message.id)
                                        }
                                        openedCount={message.openedCount}
                                        clickCount={message.clickCount}
                                        senderName={message.senderName}
                                        headerActions={
                                            canReply ? (
                                                <SentMessageReplyButtons
                                                    message={message}
                                                    messageId={
                                                        message.originalSent
                                                            .id as SentMessageId
                                                    }
                                                    onReply={handleReply}
                                                    onReplyAll={handleReplyAll}
                                                />
                                            ) : undefined
                                        }
                                    />
                                );
                            }
                            if (message.type === "received" && message.originalReceived) {
                                return (
                                    <ReceivedMessageInlineDetailLegacy
                                        key={message.id}
                                        message={toReceivedMessage(
                                            message.originalReceived,
                                        )}
                                        entityId={entityId}
                                        onCollapse={() =>
                                            toggleMessageExpanded(message.id)
                                        }
                                        headerActions={
                                            canReply ? (
                                                <ReceivedMessageReplyButtons
                                                    message={message}
                                                    entityId={entityId}
                                                    onReply={handleReply}
                                                    onReplyAll={handleReplyAll}
                                                />
                                            ) : undefined
                                        }
                                    />
                                );
                            }
                        }

                        return (
                            <div
                                key={message.id}
                                className="flex items-center gap-3 px-3 py-2 pl-8 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleMessageExpanded(message.id)}>
                                <ChevronRightIcon
                                    className="h-3 w-3 text-gray-400 flex-shrink-0 transition-transform duration-300"
                                    aria-hidden="true"
                                />
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                    <span className="font-medium text-sm text-gray-900 truncate max-w-[120px]">
                                        {message.senderName}
                                    </span>
                                    <span className="text-sm text-gray-500 truncate flex-1">
                                        {message.bodyPreview}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                                    {message.attachmentCount > 0 && (
                                        <span className="inline-flex items-center gap-0.5">
                                            <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" />
                                            {message.attachmentCount}
                                        </span>
                                    )}
                                    {message.type === "sent" && (
                                        <>
                                            {enhancedMode && (
                                                <>
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <EnvelopeOpenIcon className="h-3.5 w-3.5 text-gray-400" />
                                                        {message.openedCount}
                                                    </span>
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <CursorArrowRaysIcon className="h-3.5 w-3.5 text-gray-400" />
                                                        {message.clickCount}
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    )}
                                    {message.type === "sent" && message.originalSent && (
                                        <SentMessageStatusIndicator
                                            messageType={
                                                message.originalSent
                                                    .messageType as SentMessageType
                                            }
                                            status={message.originalSent.status}
                                            latestEventStatus={
                                                message.originalSent.latestEventStatus
                                            }
                                        />
                                    )}
                                    <span className="min-w-[75px] text-right">
                                        {relativeTimeAgo(message.timestamp)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                    {hiddenCount > 0 && (
                        <div
                            className={`px-3 py-2 pl-8 text-sm cursor-pointer border-b border-gray-100 ${defaultLinkStyle}`}
                            onClick={handleToggleExpand}>
                            {expanded
                                ? "Show fewer messages"
                                : `+${hiddenCount} older ${hiddenCount === 1 ? "message" : "messages"}`}
                        </div>
                    )}
                </div>
            </div>
            {replyState && entityId && entityType && (
                <MessageCompose
                    entityId={entityId}
                    entityType={entityType}
                    initialRecipients={replyState.initialRecipients}
                    initialCc={replyState.initialCc}
                    initialSubject={replyState.initialSubject}
                    initialBody={replyState.initialBody}
                    inReplyToUserMailboxMessageId={
                        replyState.inReplyToUserMailboxMessageId
                    }
                    inReplyToSentMessageId={replyState.inReplyToSentMessageId}
                    replyAction={replyState.replyAction}
                    onDismiss={() => setReplyState(null)}
                />
            )}
        </li>
    );
};
